// src/components/cards/RSICard.tsx
import React from 'react';
import { RSIData } from '../../types';
import { LineChart } from '../charts';
import { subDays, format } from 'date-fns';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface RSICardProps {
  data: RSIData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  overboughtThreshold: number;
  oversoldThreshold: number;
  dataStatus?: DataStatus;
}

const RSICard: React.FC<RSICardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  overboughtThreshold,
  oversoldThreshold,
  dataStatus = 'live'
}) => {
  // Debugging: Log received data
  console.log('RSICard received data:', data);
  if (data) {
    console.log('RSICard historicalData:', data.historicalData);
  }

  // Get market condition based on RSI value
  const getMarketCondition = (value: number): { label: string; color: string } => {
    if (value >= overboughtThreshold) {
      return { 
        label: 'Overbought', 
        color: '#EA3943' // Red
      };
    }
    if (value <= oversoldThreshold) {
      return { 
        label: 'Oversold', 
        color: '#16C784' // Green
      };
    }
    return { 
      label: 'Neutral', 
      color: '#8A92B2' // Neutral gray
    };
  };

  // Get color for RSI value
  const getRSIColor = (value: number): string => {
    if (value >= overboughtThreshold) return '#EA3943'; // Red
    if (value <= oversoldThreshold) return '#16C784'; // Green
    
    // Gradient between oversold and overbought
    const normalizedValue = (value - oversoldThreshold) / (overboughtThreshold - oversoldThreshold);
    if (normalizedValue < 0.5) {
      return `rgba(22, 199, 132, ${1 - normalizedValue * 2})`; // Fade from green
    } else {
      return `rgba(234, 57, 67, ${(normalizedValue - 0.5) * 2})`; // Fade to red
    }
  };

  // Custom Tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-2 bg-gray-800 rounded-md shadow-lg text-white text-sm">
          <p className="font-bold mb-1">{format(new Date(label), 'MMM d, yyyy')}</p>
          <p className="text-blue-400">RSI: {payload[0].value.toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };

  // Prepare chart data, with fallback for missing/empty historicalData
  let chartData = data?.historicalData || [];
  if (data && (!data.historicalData || data.historicalData.length === 0)) {
    console.log('RSICard: historicalData is missing or empty. Generating fallback chart data.');
    // Create minimal fallback data to ensure chart renders
    const now = data.timestamp || Date.now();
    const currentValue = data.value;
    chartData = [
      { timestamp: subDays(now, 7).getTime(), value: Math.max(0, Math.min(100, currentValue - 2)) }, // 7 days ago, slightly lower
      { timestamp: subDays(now, 1).getTime(), value: Math.max(0, Math.min(100, currentValue - 1)) }, // 1 day ago, slightly lower
      { timestamp: now, value: currentValue }, // Current
    ];
  }

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Ethereum RSI</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* RSI Value and Condition */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-3xl font-bold">{data.value.toFixed(1)}</div>
            <div 
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: `${getMarketCondition(data.value).color}20`,
                color: getMarketCondition(data.value).color
              }}
            >
              {getMarketCondition(data.value).label}
            </div>
          </div>

          {/* RSI Gauge */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
              <span>Oversold ({oversoldThreshold})</span>
              <span>Neutral</span>
              <span>Overbought ({overboughtThreshold})</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full" 
                style={{
                  width: `${(data.value / 100) * 100}%`,
                  backgroundColor: getRSIColor(data.value)
                }}
              />
            </div>
          </div>

          {/* Timeframe */}
          <div className="mb-4 text-sm">
            <span className="text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
              Timeframe:
            </span>
            <span className="ml-2 font-medium">
              {data.timeframe}
            </span>
          </div>

          {/* RSI Chart */}
          <div className="mb-3">
            <LineChart
              data={data.historicalData || []}
              color={getRSIColor(data.value)}
              gradientColors={['rgba(98, 126, 234, 0.3)', 'rgba(98, 126, 234, 0)']}
              height={128}
              formatValue={(value) => `${value.toFixed(1)}`}
              formatTimestamp={(timestamp) => new Date(timestamp).toLocaleDateString()}
              showArea={true}
              showPoints={false}
            />
          </div>

          {/* Last Updated */}
          <div className="mt-2 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No RSI data available</p>
        </div>
      )}
    </div>
  );
};

export default RSICard;
